import { useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router";
import { APP_TABS, type Tab } from "@design-lab/shared/ui/layouts/state";
import { useCurrentComposition } from "@design-lab/entities/composition";
import { useResponsive } from "./useResponsive";
import { useVisitedTabs } from "./useVisitedTabs";
import { useIsSample } from "./useIsSample";

export function useTabNavigation() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [, setVisitedTabs] = useVisitedTabs();
  const isSample = useIsSample();
  const { isDesktop } = useResponsive();
  const { currentProduct } = useCurrentComposition();
  const { sides } = currentProduct?.surfaceMeta || {};

  const tabFromURL = searchParams.get("tab") as Tab | undefined;

  const uploadArtworkUrls = searchParams.get("upload_urls");
  let initialTab = APP_TABS[0]!.value;

  if (!tabFromURL && uploadArtworkUrls) {
    try {
      const urlsObj = JSON.parse(uploadArtworkUrls) as Record<string, string>;
      if (urlsObj["front"]) {
        initialTab = "front";
      } else if (urlsObj["back"]) {
        initialTab = "back";
      }
    } catch {
      // If parsing fails, use default fallback
    }
  }

  const fallbackTab = initialTab;
  const tab: Tab = APP_TABS.some(t => t.value === tabFromURL) ? tabFromURL! : fallbackTab;

  useEffect(() => {
    setVisitedTabs((prev: Set<Tab>) => new Set(prev).add(tab));
  }, [setVisitedTabs, tab]);

  useEffect(() => {
    if (tabFromURL) return;

    const next = new URLSearchParams(searchParams);
    next.set("tab", fallbackTab);
    setSearchParams(next, { replace: true });
  }, [tabFromURL, fallbackTab, searchParams, setSearchParams]);

  const setTab = useCallback(
    (newTab: Tab) => {
      if (newTab === tab) return;
      const next = new URLSearchParams(searchParams);
      next.set("tab", newTab);
      setSearchParams(next);
    },
    [tab, searchParams, setSearchParams]
  );

  const currentIndex = APP_TABS.findIndex(t => t.value === tab);
  const currentTab = APP_TABS[currentIndex];
  const nextTabRaw = APP_TABS[(currentIndex + 1) % APP_TABS.length];
  const prevTabRaw = currentIndex > 0 ? APP_TABS[currentIndex - 1] : undefined;

  // Apply sample mode transformation to nextTab
  const nextTab = nextTabRaw
    ? {
        ...nextTabRaw,
        title: isSample && nextTabRaw.value === "products" ? "Colors" : nextTabRaw.title,
        mobileTitle: isSample && nextTabRaw.value === "products" ? "Colors" : nextTabRaw.mobileTitle
      }
    : undefined;

  // Apply sample mode transformation to prevTab
  const prevTab = prevTabRaw
    ? {
        ...prevTabRaw,
        title: isSample && prevTabRaw.value === "products" ? "Colors" : prevTabRaw.title,
        mobileTitle: isSample && prevTabRaw.value === "products" ? "Colors" : prevTabRaw.mobileTitle
      }
    : undefined;

  let canBeSkipped = Boolean(currentTab?.canBeSkipped?.({ sides, isDesktop }));
  let canGoNext = currentIndex < APP_TABS.length - 1;
  const canGoBack = currentIndex > 0;
  const hasBackSide = Boolean(sides?.back?.length);
  const isFrontTab = currentTab?.value === "front";

  if (isFrontTab && !hasBackSide) {
    canGoNext = false;
    canBeSkipped = false;
  }

  const isDesignTab = useMemo(() => tab === "front" || tab === "back", [tab]);
  const isProductsTab = useMemo(() => tab === "products", [tab]);
  const isSizeTab = useMemo(() => tab === "size", [tab]);

  const back = useCallback(() => {
    if (prevTabRaw) {
      setTab(prevTabRaw.value);
    }
  }, [prevTabRaw, setTab]);

  return {
    tab,
    setTab,
    currentIndex,
    currentTab,
    nextTab,
    nextTabRaw,
    prevTab,
    prevTabRaw,
    canBeSkipped,
    canGoNext,
    canGoBack,
    back,
    isDesignTab,
    isProductsTab,
    isSizeTab,
    isSample
  };
}
